import { ApiError } from '@eneco/dc/client/ApiError';
import { discontinueIntake, getDiscontinueReasons } from '@eneco/dc/services/smartthermostat';
import {
  DiscontinueIntakeResponse,
  ReasonDto,
  ResponseDataOfDiscontinueIntakeResponse,
  ResponseDataOfGetDiscontinueReasonsResponse,
} from '@monorepo-types/dc/smartthermostat';

import {
  CancellationReasonFetchValues,
  CancellationReasonMachineContextBase,
  createCancellationReasonMachineWithFetch,
} from './cancellationReasonMachine';
import { MachineConfig } from '../types';

export type SmartThermostatCancellationReasonMachineContext = CancellationReasonMachineContextBase<
  ReasonDto,
  DiscontinueIntakeResponse
>;

interface SmartThermostatReasonFetchOutput {
  intakeData: ResponseDataOfDiscontinueIntakeResponse;
  reasonsData: ResponseDataOfGetDiscontinueReasonsResponse;
}

const getIntakeService = (
  config: MachineConfig,
  customerObject: CancellationReasonFetchValues,
): Promise<ResponseDataOfDiscontinueIntakeResponse> =>
  new Promise((resolve, reject) =>
    discontinueIntake({
      businessUnit: config.businessUnit,
      label: config.label,
      customerId: customerObject.customerId!,
    })
      .then(response => resolve(response))
      .catch((error: ApiError) => reject(new Error(error?.message))),
  );

const getReasonsService = (config: MachineConfig): Promise<ResponseDataOfGetDiscontinueReasonsResponse> =>
  new Promise((resolve, reject) =>
    getDiscontinueReasons({
      businessUnit: config.businessUnit,
      label: config.label,
    })
      .then(response => resolve(response))
      .catch((error: ApiError) => reject(new Error(error?.message))),
  );

const getValuesFromAttributes = (
  intakeResponse?: DiscontinueIntakeResponse,
  reasonsResponse?: ResponseDataOfGetDiscontinueReasonsResponse,
): Partial<SmartThermostatCancellationReasonMachineContext> => {
  if (!intakeResponse) {
    return {};
  }

  return {
    intake: intakeResponse,
    reasons: reasonsResponse?.data?.reasons ?? undefined,
  };
};

export const createSmartThermostatCancellationReasonMachine = () =>
  createCancellationReasonMachineWithFetch<ReasonDto, DiscontinueIntakeResponse, SmartThermostatReasonFetchOutput>({
    fetch: (config, values) =>
      Promise.all([getIntakeService(config, values), getReasonsService(config)]).then(([intakeData, reasonsData]) => ({
        intakeData,
        reasonsData,
      })),
    mapContext: ({ intakeData, reasonsData }) => getValuesFromAttributes(intakeData?.data, reasonsData),
    isIntakeDisallowed: ({ intakeData }) => intakeData.data?.allowed === false,
  });
